import React, { useState, useEffect, useRef } from 'react';
import API from '../../services/api';
import { Trash2, Edit3, Eye, Search, Filter, AlertTriangle, X, CheckCircle, Loader2, Ban, Plus, ChevronLeft, ChevronRight } from 'lucide-react';

// Resolve the logged-in user's ID from wherever your auth layer stores it.
// The backend is the real gatekeeper (it checks the `permissions` table),
// but we still need a real userId here instead of a hardcoded one —
// sending a fake/mock ID would defeat the permission check entirely.
const getCurrentUserId = () => {
    try {
        const direct = localStorage.getItem('userId') || localStorage.getItem('user_id');
        if (direct) return Number(direct);

        const rawUser = localStorage.getItem('user');
        if (rawUser) {
            const parsed = JSON.parse(rawUser);
            if (parsed?.id) return Number(parsed.id);
        }
    } catch (err) {
        console.error('Could not resolve current user id:', err);
    }
    return null;
};

// Small embedded stylesheet for polish that inline styles can't express well
// (hover states, focus rings, a spin animation) without pulling in a CSS framework.
const GLOBAL_STYLES = `
    .vch-row { transition: background-color 0.12s ease; }
    .vch-row:hover { background-color: #f8fafc; }
    .vch-action-btn { transition: transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease; }
    .vch-action-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 3px 8px rgba(15,23,42,0.12); }
    .vch-action-btn:disabled { opacity: 0.6; }
    .vch-spin { animation: vch-spin 0.8s linear infinite; }
    @keyframes vch-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    input:focus, select:focus { border-color: #1c313a !important; box-shadow: 0 0 0 3px rgba(28,49,58,0.12); }
    button:disabled { cursor: not-allowed; }
    .vch-table td { padding: 12px 14px; vertical-align: middle; color: #1e293b; }
    .vch-dropdown-item:hover { background-color: #f1f5f9; }
`;

// Color-codes the voucher type badge so voucher types are scannable at a glance.
const VOUCHER_TYPE_COLORS = {
    CPV: { background: '#fee2e2', color: '#b91c1c' },
    CRV: { background: '#dcfce7', color: '#15803d' },
    BPV: { background: '#fef3c7', color: '#b45309' },
    BRV: { background: '#dbeafe', color: '#1d4ed8' },
    JV:  { background: '#ede9fe', color: '#6d28d9' }
};
const voucherTypeBadgeStyle = (type) => VOUCHER_TYPE_COLORS[type] || { background: '#e2e8f0', color: '#334155' };

// This must match the `type` value stored in the `permissions` table for
// voucher-module rights (e.g. permissions rows where type = 'voucher').
// Without filtering by this, a user's can_edit/can_delete from some other
// module could wrongly grant (or block) voucher actions.
const PERMISSION_MODULE = 'voucher';

// Same searchable-account pattern used on the voucher generation screen
// (hits /vouchers/search), reused here so editing an entry's account is a
// real lookup instead of a locked, read-only label.
const AccountSearchSelect = ({ accountId, accountTitle, onSelect }) => {
    const [searchTerm, setSearchTerm] = useState(accountTitle || '');
    const [options, setOptions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        setSearchTerm(accountTitle || '');
    }, [accountTitle]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const delay = setTimeout(async () => {
            try {
                const res = await API.get(`/vouchers/search?search=${encodeURIComponent(searchTerm)}&limit=50`);
                setOptions(res.data?.data || []);
            } catch (err) {
                console.error('Account search failed:', err);
                setOptions([]);
            }
        }, 150);
        return () => clearTimeout(delay);
    }, [searchTerm, isOpen]);

    return (
        <div ref={wrapperRef} style={styles.searchSelectWrapper}>
            <input
                type="text"
                style={styles.input}
                placeholder="Search account..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
                onFocus={() => setIsOpen(true)}
                required
            />
            {!accountId && searchTerm && (
                <span style={styles.searchSelectWarning} title="Pick an account from the dropdown">
                    <AlertTriangle size={13} />
                </span>
            )}
            {isOpen && options.length > 0 && (
                <div style={styles.dropdownList}>
                    {options.map(opt => (
                        <div
                            key={opt.id}
                            className="vch-dropdown-item"
                            style={styles.dropdownItem}
                            onMouseDown={() => {
                                onSelect(opt.id, opt.title);
                                setSearchTerm(opt.title);
                                setIsOpen(false);
                            }}
                        >
                            <div style={{ fontWeight: 700, color: '#1c313a', fontSize: '13px' }}>{opt.title}</div>
                            <div style={{ fontSize: '10px', color: '#78909c' }}>ID: {opt.id}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const VoucherList = () => {
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ search: '', voucher_type: '', start_date: '', end_date: '' });

    // Server-side pagination — total/totalPages come back from the API
    // and are kept in sync with page/limit on every fetch.
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });

    // Modal states
    const [selectedVoucher, setSelectedVoucher] = useState(null);
    const [voucherTransactions, setVoucherTransactions] = useState([]);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);

    // Edit Modal states
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editForm, setEditForm] = useState({ id: '', voucher_no: '', voucher_type: '', transaction_date: '', narration: '', entries: [] });
    const [editError, setEditError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Row-level delete loading state
    const [deletingId, setDeletingId] = useState(null);

    // Actual can_edit / can_delete for this user, scoped to the voucher module.
    // Buttons are shown/enabled based on this, not just on "is someone logged in".
    const [permissions, setPermissions] = useState({ can_edit: false, can_delete: false });
    const [permLoading, setPermLoading] = useState(true);

    const currentUserId = getCurrentUserId();

    useEffect(() => {
        let cancelled = false;
        const loadPermissions = async () => {
            setPermLoading(true);
            if (!currentUserId) {
                if (!cancelled) {
                    setPermissions({ can_edit: false, can_delete: false });
                    setPermLoading(false);
                }
                return;
            }
            try {
                const res = await API.get('/vouchers/permissions', { params: { userId: currentUserId, type: PERMISSION_MODULE } });
                if (!cancelled && res.data.success) {
                    setPermissions({
                        can_edit: !!res.data.permissions.can_edit,
                        can_delete: !!res.data.permissions.can_delete
                    });
                }
            } catch (err) {
                console.error('Could not load voucher permissions:', err);
                if (!cancelled) setPermissions({ can_edit: false, can_delete: false });
            } finally {
                if (!cancelled) setPermLoading(false);
            }
        };
        loadPermissions();
        return () => { cancelled = true; };
    }, [currentUserId]);

    const fetchVouchers = async (page = pagination.page, limit = pagination.limit) => {
        setLoading(true);
        try {
            const query = new URLSearchParams({ ...filters, page, limit }).toString();
            const res = await API.get(`/vouchers/list?${query}`);
            if (res.data.success) {
                setVouchers(res.data.data);
                if (res.data.pagination) {
                    setPagination(prev => ({ ...prev, ...res.data.pagination }));
                }
            }
        } catch (err) {
            console.error("Error fetching vouchers:", err);
            alert("Failed to load voucher list.");
        } finally {
            setLoading(false);
        }
    };

    // Refetch whenever the page or page size changes (covers initial load too,
    // since it runs on mount with page=1/limit=20).
    useEffect(() => {
        fetchVouchers(pagination.page, pagination.limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, pagination.limit]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const applyFilters = (e) => {
        e.preventDefault();
        // Filters always jump back to page 1. If we're already there, the
        // page/limit effect above won't re-fire on its own, so fetch directly.
        if (pagination.page === 1) {
            fetchVouchers(1, pagination.limit);
        } else {
            setPagination(prev => ({ ...prev, page: 1 }));
        }
    };

    // View Details
    const handleViewDetails = async (id) => {
        try {
            const res = await API.get(`/vouchers/${id}`);
            if (res.data.success) {
                setSelectedVoucher(res.data.voucher);
                setVoucherTransactions(res.data.transactions);
                setIsViewModalOpen(true);
            }
        } catch (err) {
            alert("Error fetching voucher details.");
        }
    };

    // Open Edit Modal
    const handleOpenEdit = async (voucher) => {
        if (!currentUserId || !permissions.can_edit) {
            alert("You do not have permission to edit vouchers.");
            return;
        }
        setEditError('');
        try {
            const res = await API.get(`/vouchers/${voucher.id}`);
            if (res.data.success) {
                setEditForm({
                    id: voucher.id,
                    voucher_no: voucher.voucher_no,
                    voucher_type: voucher.voucher_type,
                    transaction_date: voucher.transaction_date.split('T')[0],
                    narration: voucher.narration || '',
                    entries: res.data.transactions.map(t => ({
                        account_id: t.account_id,
                        account_title: t.account_title || `Account #${t.account_id}`,
                        debit: Number(t.debit) > 0 ? String(t.debit) : '',
                        credit: Number(t.credit) > 0 ? String(t.credit) : '',
                        invoice_no: t.invoice_no || '',
                        description: t.description || ''
                    }))
                });
                setIsEditModalOpen(true);
            }
        } catch (err) {
            alert("Could not load data for editing.");
        }
    };

    // Update one field on one entry, keeping Debit/Credit mutually exclusive
    // (an entry can't be both a debit and a credit line at once).
    const handleEntryChange = (idx, field, value) => {
        setEditForm(prev => {
            const entries = [...prev.entries];
            const entry = { ...entries[idx], [field]: value };
            if (field === 'debit' && Number(value) > 0) entry.credit = '';
            if (field === 'credit' && Number(value) > 0) entry.debit = '';
            entries[idx] = entry;
            return { ...prev, entries };
        });
    };

    // Re-point an entry at a different account, same lookup used to build
    // vouchers in the first place (/vouchers/search).
    const handleEntryAccountChange = (idx, accountId, accountTitle) => {
        setEditForm(prev => {
            const entries = [...prev.entries];
            entries[idx] = { ...entries[idx], account_id: accountId, account_title: accountTitle };
            return { ...prev, entries };
        });
    };

    const addEditEntry = () => {
        setEditForm(prev => ({
            ...prev,
            entries: [
                ...prev.entries,
                { account_id: '', account_title: '', debit: '', credit: '', invoice_no: '', description: prev.narration || '' }
            ]
        }));
    };

    // A voucher needs at least one Dr and one Cr line to ever balance, so
    // don't allow removing down below 2 entries.
    const removeEditEntry = (idx) => {
        setEditForm(prev => {
            if (prev.entries.length <= 2) return prev;
            return { ...prev, entries: prev.entries.filter((_, i) => i !== idx) };
        });
    };

    const editTotals = editForm.entries.reduce(
        (acc, en) => {
            acc.debit += Number(en.debit) || 0;
            acc.credit += Number(en.credit) || 0;
            return acc;
        },
        { debit: 0, credit: 0 }
    );
    const editDifference = Math.round((editTotals.debit - editTotals.credit) * 100) / 100;
    const isEditBalanced = Math.abs(editDifference) < 0.01 && editTotals.debit > 0;

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setEditError('');

        if (!currentUserId) {
            setEditError("Could not identify the logged-in user. Please log in again before editing.");
            return;
        }
        if (!editForm.transaction_date) {
            setEditError("Transaction date is required.");
            return;
        }
        const missingAccount = editForm.entries.find(en => !en.account_id);
        if (missingAccount) {
            setEditError("Every entry needs an account selected from the search dropdown.");
            return;
        }
        const emptyEntry = editForm.entries.find(en => (Number(en.debit) || 0) === 0 && (Number(en.credit) || 0) === 0);
        if (emptyEntry) {
            setEditError("Every entry needs either a Debit or a Credit amount.");
            return;
        }
        if (!isEditBalanced) {
            setEditError(`Voucher is not balanced. Total Debit (${editTotals.debit.toFixed(2)}) must equal Total Credit (${editTotals.credit.toFixed(2)}).`);
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                transaction_date: editForm.transaction_date,
                narration: editForm.narration,
                entries: editForm.entries.map(en => ({
                    account_id: en.account_id,
                    description: en.description,
                    debit: Number(en.debit) || 0,
                    credit: Number(en.credit) || 0,
                    invoice_no: en.invoice_no || null
                })),
                userId: currentUserId,
                type: PERMISSION_MODULE,
                ip_address: '127.0.0.1'
            };
            const res = await API.put(`/vouchers/${editForm.id}`, payload);
            if (res.data.success) {
                setIsEditModalOpen(false);
                fetchVouchers(pagination.page, pagination.limit);
            }
        } catch (err) {
            setEditError(err.response?.data?.error || "Failed to update voucher.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Delete Voucher — the backend re-checks permission itself; this guard
    // just avoids sending a request we already know will be rejected because
    // we have no identified user.
    const handleDelete = async (id, voucherNo) => {
        if (!currentUserId) {
            alert("Could not identify the logged-in user. Please log in again before deleting.");
            return;
        }
        if (!permissions.can_delete) {
            alert("You do not have permission to delete vouchers.");
            return;
        }
        if (!window.confirm(`Are you sure you want to delete voucher ${voucherNo}? This cannot be undone.`)) return;

        setDeletingId(id);
        try {
            const payload = { userId: currentUserId, type: PERMISSION_MODULE, ip_address: '127.0.0.1' };
            const res = await API.delete(`/vouchers/${id}`, { data: payload });
            if (res.data.success) {
                // If that was the last row on a page beyond the first, step
                // back a page instead of landing on an empty page.
                if (vouchers.length === 1 && pagination.page > 1) {
                    setPagination(prev => ({ ...prev, page: prev.page - 1 }));
                } else {
                    fetchVouchers(pagination.page, pagination.limit);
                }
            }
        } catch (err) {
            alert(err.response?.data?.error || "Delete failed. Check your permissions.");
        } finally {
            setDeletingId(null);
        }
    };

    const showPermissionBanner = !permLoading && (!currentUserId || !(permissions.can_edit || permissions.can_delete));

    return (
        <div style={styles.container}>
            <style>{GLOBAL_STYLES}</style>

            <div style={styles.headerArea}>
                <h2 style={styles.pageTitle}>📋 Voucher List &amp; Management</h2>
                <p style={styles.pageSubtitle}>Manage, inspect, edit, or delete existing financial vouchers securely with permission logging.</p>
                {showPermissionBanner && (
                    <div style={styles.noUserBanner}>
                        <AlertTriangle size={16} />
                        <span>
                            {!currentUserId
                                ? "No logged-in user detected — Edit and Delete are disabled until you sign in."
                                : "You don't have Edit or Delete rights for vouchers. Contact an admin if you need access."}
                        </span>
                    </div>
                )}
            </div>

            {/* FILTER FORM */}
            <form onSubmit={applyFilters} style={styles.filterCard}>
                <div style={styles.filterGrid}>
                    <input
                        type="text"
                        name="search"
                        placeholder="Search Voucher No or Narration..."
                        value={filters.search}
                        onChange={handleFilterChange}
                        style={styles.input}
                    />
                    <select name="voucher_type" value={filters.voucher_type} onChange={handleFilterChange} style={styles.input}>
                        <option value="">All Types</option>
                        <option value="CPV">CPV (Cash Payment)</option>
                        <option value="CRV">CRV (Cash Receipt)</option>
                        <option value="BPV">BPV (Bank Payment)</option>
                        <option value="BRV">BRV (Bank Receipt)</option>
                        <option value="JV">JV (Journal Voucher)</option>
                    </select>
                    <input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} style={styles.input} />
                    <input type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} style={styles.input} />
                </div>
                <button type="submit" style={styles.btnPrimary}><Filter size={16}/> Filter Vouchers</button>
            </form>

            {/* VOUCHER TABLE */}
            <div style={styles.tableWrapper}>
                <table style={styles.table} className="vch-table">
                    <thead>
                        <tr style={styles.th}>
                            <th>ID</th>
                            <th>Voucher No</th>
                            <th>Type</th>
                            <th>Date</th>
                            <th>Narration</th>
                            <th style={{textAlign: 'right'}}>Total Amount</th>
                            <th style={{textAlign: 'center'}}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" style={{textAlign: 'center', padding: '24px', color: '#64748b'}}>Loading vouchers...</td></tr>
                        ) : vouchers.length === 0 ? (
                            <tr><td colSpan="7" style={{textAlign: 'center', padding: '24px', color: '#94a3b8'}}>No vouchers found.</td></tr>
                        ) : (
                            vouchers.map((v) => {
                                const canEditThis = currentUserId && permissions.can_edit;
                                const canDeleteThis = currentUserId && permissions.can_delete;
                                const editTitle = !currentUserId ? "Login required to edit" : !permissions.can_edit ? "You don't have edit permission" : "Edit Voucher";
                                const deleteTitle = !currentUserId ? "Login required to delete" : !permissions.can_delete ? "You don't have delete permission" : "Delete Voucher";

                                return (
                                    <tr key={v.id} style={styles.tr} className="vch-row">
                                        <td>{v.id}</td>
                                        <td><b>{v.voucher_no}</b></td>
                                        <td><span style={{...styles.badge, ...voucherTypeBadgeStyle(v.voucher_type)}}>{v.voucher_type}</span></td>
                                        <td>{v.transaction_date?.split('T')[0]}</td>
                                        <td>{v.narration || '-'}</td>
                                        <td style={{textAlign: 'right', fontWeight: 'bold'}}>{Number(v.total_amount).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                        <td style={{textAlign: 'center', display: 'flex', gap: '8px', justifyContent: 'center'}}>
                                            <button onClick={() => handleViewDetails(v.id)} style={styles.actionBtnBlue} className="vch-action-btn" title="View Detail"><Eye size={16}/></button>
                                            <button
                                                onClick={() => handleOpenEdit(v)}
                                                style={canEditThis ? styles.actionBtnGreen : styles.actionBtnDisabled}
                                                className="vch-action-btn"
                                                title={editTitle}
                                                disabled={permLoading || !canEditThis}
                                            ><Edit3 size={16}/></button>
                                            <button
                                                onClick={() => handleDelete(v.id, v.voucher_no)}
                                                style={canDeleteThis ? styles.actionBtnRed : styles.actionBtnDisabled}
                                                className="vch-action-btn"
                                                title={deleteTitle}
                                                disabled={permLoading || !canDeleteThis || deletingId === v.id}
                                            >
                                                {deletingId === v.id ? <Loader2 size={16} className="vch-spin" /> : <Trash2 size={16}/>}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* PAGINATION */}
            <div style={styles.paginationBar}>
                <span style={styles.paginationInfo}>
                    {pagination.total === 0
                        ? (loading ? 'Loading…' : 'No vouchers')
                        : `Showing ${(pagination.page - 1) * pagination.limit + 1}–${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total}`}
                </span>
                <div style={styles.paginationControls}>
                    <select
                        value={pagination.limit}
                        onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                        style={styles.pageSizeSelect}
                    >
                        <option value={10}>10 / page</option>
                        <option value={20}>20 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                    </select>
                    <button
                        type="button"
                        onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                        disabled={pagination.page <= 1 || loading}
                        style={pagination.page <= 1 || loading ? styles.pageBtnDisabled : styles.pageBtn}
                    ><ChevronLeft size={16}/></button>
                    <span style={styles.pageIndicator}>Page {pagination.page} of {pagination.totalPages}</span>
                    <button
                        type="button"
                        onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                        disabled={pagination.page >= pagination.totalPages || loading}
                        style={pagination.page >= pagination.totalPages || loading ? styles.pageBtnDisabled : styles.pageBtn}
                    ><ChevronRight size={16}/></button>
                </div>
            </div>

            {/* VIEW DETAIL MODAL */}
            {isViewModalOpen && selectedVoucher && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <div style={styles.modalHeader}>
                            <h3>Voucher Details: {selectedVoucher.voucher_no}</h3>
                            <button onClick={() => setIsViewModalOpen(false)} style={styles.closeBtn}><X size={18}/></button>
                        </div>
                        <p><b>Type:</b> {selectedVoucher.voucher_type} | <b>Date:</b> {selectedVoucher.transaction_date.split('T')[0]}</p>
                        <p><b>Narration:</b> {selectedVoucher.narration}</p>

                        <h4>Transactions Breakdown:</h4>
                        <table style={styles.table} className="vch-table">
                            <thead>
                                <tr style={styles.th}>
                                    <th>Account</th>
                                    <th>Description</th>
                                    <th style={{textAlign: 'right'}}>Debit</th>
                                    <th style={{textAlign: 'right'}}>Credit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {voucherTransactions.map((t, idx) => (
                                    <tr key={idx} style={styles.tr}>
                                        <td>{t.account_title || `Account ID: ${t.account_id}`}</td>
                                        <td>{t.description}</td>
                                        <td style={{textAlign: 'right'}}>{Number(t.debit).toLocaleString()}</td>
                                        <td style={{textAlign: 'right'}}>{Number(t.credit).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button onClick={() => setIsViewModalOpen(false)} style={{...styles.btnPrimary, marginTop: '15px'}}>Close</button>
                    </div>
                </div>
            )}

            {/* EDIT MODAL */}
            {isEditModalOpen && (
                <div style={styles.modalOverlay}>
                    <div style={styles.editModalContent}>
                        <div style={styles.editModalHeader}>
                            <div>
                                <h3 style={{margin: 0}}>Edit Voucher</h3>
                                <div style={styles.editModalSubtitle}>
                                    <span style={styles.badge}>{editForm.voucher_type}</span>
                                    <span>{editForm.voucher_no}</span>
                                </div>
                            </div>
                            <button onClick={() => setIsEditModalOpen(false)} style={styles.closeBtn}><X size={18}/></button>
                        </div>

                        <form onSubmit={handleUpdateSubmit} style={styles.editForm}>
                            <div style={styles.editModalBody}>
                                <div style={styles.fieldRow}>
                                    <div style={{flex: 1}}>
                                        <label style={styles.label}>Transaction Date</label>
                                        <input
                                            type="date"
                                            style={styles.input}
                                            value={editForm.transaction_date}
                                            onChange={e => setEditForm({...editForm, transaction_date: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div style={{flex: 2}}>
                                        <label style={styles.label}>General Narration</label>
                                        <input
                                            type="text"
                                            style={styles.input}
                                            value={editForm.narration}
                                            onChange={e => setEditForm({...editForm, narration: e.target.value})}
                                            placeholder="Overall description for this voucher"
                                        />
                                    </div>
                                </div>

                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', marginBottom: '10px'}}>
                                    <label style={{...styles.label, display: 'block', marginTop: 0}}>Voucher Entries</label>
                                    <button type="button" onClick={addEditEntry} style={styles.addEntryBtn}>
                                        <Plus size={14}/> Add Entry
                                    </button>
                                </div>

                                {editForm.entries.map((entry, idx) => (
                                    <div key={idx} style={styles.entryCard}>
                                        <div style={styles.entryCardTop}>
                                            <span style={styles.entryIndex}>Entry {idx + 1}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeEditEntry(idx)}
                                                disabled={editForm.entries.length <= 2}
                                                style={editForm.entries.length <= 2 ? styles.removeEntryBtnDisabled : styles.removeEntryBtn}
                                                title={editForm.entries.length <= 2 ? "A voucher needs at least 2 entries" : "Remove this entry"}
                                            >
                                                <Trash2 size={13}/> Remove
                                            </button>
                                        </div>

                                        <label style={styles.miniLabel}>Account</label>
                                        <AccountSearchSelect
                                            accountId={entry.account_id}
                                            accountTitle={entry.account_title}
                                            onSelect={(id, title) => handleEntryAccountChange(idx, id, title)}
                                        />

                                        <div style={styles.entryFieldGrid}>
                                            <div>
                                                <label style={styles.miniLabel}>Description</label>
                                                <input
                                                    type="text"
                                                    placeholder="Description"
                                                    value={entry.description}
                                                    onChange={e => handleEntryChange(idx, 'description', e.target.value)}
                                                    style={styles.input}
                                                />
                                            </div>
                                            <div>
                                                <label style={styles.miniLabel}>Invoice #</label>
                                                <input
                                                    type="text"
                                                    placeholder="Invoice #"
                                                    value={entry.invoice_no}
                                                    onChange={e => handleEntryChange(idx, 'invoice_no', e.target.value)}
                                                    style={styles.input}
                                                />
                                            </div>
                                            <div>
                                                <label style={styles.miniLabel}>Debit</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                    value={entry.debit}
                                                    onChange={e => handleEntryChange(idx, 'debit', e.target.value)}
                                                    style={{...styles.input, textAlign: 'right'}}
                                                />
                                            </div>
                                            <div>
                                                <label style={styles.miniLabel}>Credit</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                    value={entry.credit}
                                                    onChange={e => handleEntryChange(idx, 'credit', e.target.value)}
                                                    style={{...styles.input, textAlign: 'right'}}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div style={styles.totalsCard}>
                                    <span style={styles.totalsLabel}>Totals</span>
                                    <span style={styles.totalsValue}>Debit: <b>{editTotals.debit.toFixed(2)}</b></span>
                                    <span style={styles.totalsValue}>Credit: <b>{editTotals.credit.toFixed(2)}</b></span>
                                </div>

                                <div style={isEditBalanced ? styles.balanceOk : styles.balanceBad}>
                                    {isEditBalanced ? <CheckCircle size={16}/> : <AlertTriangle size={16}/>}
                                    <span>
                                        {isEditBalanced
                                            ? 'Voucher is balanced.'
                                            : `Out of balance by ${Math.abs(editDifference).toFixed(2)} — Debit and Credit totals must match.`}
                                    </span>
                                </div>

                                {editError && (
                                    <div style={styles.errorBanner}>
                                        <Ban size={16}/>
                                        <span>{editError}</span>
                                    </div>
                                )}
                            </div>

                            <div style={styles.editModalFooter}>
                                <button type="button" onClick={() => setIsEditModalOpen(false)} style={styles.btnSecondary} disabled={isSubmitting}>
                                    Cancel
                                </button>
                                <button type="submit" style={styles.btnSuccess} disabled={isSubmitting || !isEditBalanced}>
                                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: { padding: '24px', background: '#f1f5f9', minHeight: '100vh', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
    headerArea: { marginBottom: '22px' },
    pageTitle: { margin: 0, fontSize: '22px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' },
    pageSubtitle: { margin: '6px 0 0', fontSize: '13.5px', color: '#64748b' },
    noUserBanner: { display: 'flex', alignItems: 'center', gap: '8px', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '10px 14px', borderRadius: '8px', marginTop: '12px', fontSize: '13px', fontWeight: 500 },
    filterCard: { background: '#fff', padding: '16px', borderRadius: '12px', display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '20px', boxShadow: '0 1px 3px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' },
    filterGrid: { display: 'flex', gap: '10px', flex: 1 },
    input: { padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', width: '100%', fontSize: '13px', fontFamily: 'inherit', transition: 'border-color 0.12s ease' },
    tableWrapper: { background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th: { background: '#0f1f26', color: '#e2e8f0', textAlign: 'left', padding: '13px 14px', fontSize: '11.5px', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 },
    tr: { borderBottom: '1px solid #f1f5f9' },
    badge: { padding: '4px 9px', borderRadius: '999px', fontWeight: 700, fontSize: '11px', letterSpacing: '0.02em' },
    btnPrimary: { background: '#1c313a', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, fontSize: '13px' },
    btnSuccess: { background: '#10b981', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
    btnSecondary: { background: '#64748b', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
    actionBtnBlue: { background: '#eff6ff', border: 'none', color: '#3b82f6', padding: '8px', borderRadius: '6px', cursor: 'pointer' },
    actionBtnGreen: { background: '#f0fdf4', border: 'none', color: '#10b981', padding: '8px', borderRadius: '6px', cursor: 'pointer' },
    actionBtnRed: { background: '#fef2f2', border: 'none', color: '#ef4444', padding: '8px', borderRadius: '6px', cursor: 'pointer' },
    actionBtnDisabled: { background: '#f1f5f9', border: 'none', color: '#94a3b8', padding: '8px', borderRadius: '6px', cursor: 'not-allowed' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(15,23,42,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { background: '#fff', padding: '25px', borderRadius: '12px', width: '600px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' },
    label: { display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em' },

    // Edit modal — dedicated, more spacious styling
    editModalContent: { background: '#fff', borderRadius: '14px', width: '820px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 45px rgba(0,0,0,0.25)', overflow: 'hidden' },
    editModalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 },
    editModalSubtitle: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', fontSize: '13px', color: '#64748b' },
    // The form must be a flex column that actually shrinks (minHeight: 0) so
    // editModalBody's overflowY:auto can scroll internally while the footer
    // (Save/Cancel) stays pinned and visible instead of being pushed past
    // the modal's maxHeight and clipped by its overflow:hidden.
    editForm: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
    editModalBody: { padding: '22px 24px', overflowY: 'auto', flex: 1, minHeight: 0 },
    editModalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 },
    fieldRow: { display: 'flex', gap: '16px', marginBottom: '6px' },

    // One bordered card per entry — every field gets its own labeled cell,
    // so nothing is ever squeezed into a single overflowing row.
    entryCard: { border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', marginBottom: '12px', background: '#fbfcfe' },
    entryCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
    entryIndex: { fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' },
    entryFieldGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginTop: '12px' },
    miniLabel: { display: 'block', fontSize: '10.5px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '4px', marginTop: '10px' },
    totalsCard: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '22px', padding: '12px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', marginTop: '6px', flexWrap: 'wrap' },
    totalsLabel: { fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em', marginRight: 'auto' },
    totalsValue: { fontSize: '13.5px', color: '#334155' },
    balanceOk: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', padding: '10px 14px', borderRadius: '8px', background: '#f0fdf4', color: '#15803d', fontSize: '13px', fontWeight: 600 },
    balanceBad: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', padding: '10px 14px', borderRadius: '8px', background: '#fff7ed', color: '#c2410c', fontSize: '13px', fontWeight: 600 },
    errorBanner: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', color: '#b91c1c', fontSize: '13px', fontWeight: 600 },

    // Account search-select (edit modal)
    searchSelectWrapper: { position: 'relative', width: '100%' },
    searchSelectWarning: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#f59e0b', pointerEvents: 'none' },
    dropdownList: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', maxHeight: '220px', overflowY: 'auto', boxShadow: '0 10px 24px rgba(15,23,42,0.15)', zIndex: 30 },
    dropdownItem: { padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' },

    // Add/remove entry buttons (edit modal)
    addEntryBtn: { display: 'flex', alignItems: 'center', gap: '5px', background: '#eef2ff', color: '#3730a3', border: 'none', padding: '7px 13px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '12px' },
    removeEntryBtn: { display: 'flex', alignItems: 'center', gap: '5px', background: '#fef2f2', color: '#ef4444', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '11.5px' },
    removeEntryBtnDisabled: { display: 'flex', alignItems: 'center', gap: '5px', background: '#f1f5f9', color: '#cbd5e1', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'not-allowed', fontWeight: 600, fontSize: '11.5px' },

    // Pagination bar
    paginationBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px', padding: '12px 16px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '10px' },
    paginationInfo: { fontSize: '13px', color: '#64748b', fontWeight: 500 },
    paginationControls: { display: 'flex', alignItems: 'center', gap: '10px' },
    pageSizeSelect: { padding: '7px 10px', borderRadius: '7px', border: '1px solid #cbd5e1', fontSize: '12.5px', fontFamily: 'inherit', outline: 'none', color: '#334155' },
    pageBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: '#f1f5f9', color: '#1c313a', border: 'none', borderRadius: '7px', cursor: 'pointer' },
    pageBtnDisabled: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: '#f8fafc', color: '#cbd5e1', border: 'none', borderRadius: '7px', cursor: 'not-allowed' },
    pageIndicator: { fontSize: '12.5px', fontWeight: 600, color: '#334155', minWidth: '90px', textAlign: 'center' }
};

export default VoucherList;
